import React, { useCallback, useEffect, useState } from 'react';
import { parsePrefs } from './shared/PreorderNotes';
import './StationScreen.css';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../config';
import { getPollMs } from '../utils/appSettings';
import i18n from '../i18n';

const tr = i18n.t.bind(i18n);
const API = (p) => `${config.API_BASE_URL}${p}`;
const COURSES = { 1: tr('Starters'), 2: tr('Mains'), 4: tr('Sides'), 5: tr('Drinks'), 3: tr('Desserts') };

const ageMin = (sentAt) => Math.max(0, Math.floor((Date.now() - new Date(sentAt).getTime()) / 60000));

// Screen-only station queue (kitchen / bar). No printers by design.
const StationScreen = ({ station: stationProp, embedded = false }) => {
  const { station: stationParam } = useParams();
  const station = stationProp || stationParam;
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(API(`/ordering/station/${station}`));
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { /* next poll retries */ }
  }, [station]);
  useEffect(() => { load(); const t = setInterval(load, getPollMs('normal')); return () => clearInterval(t); }, [load]);

  const setStatus = async (id, status) => {
    try { await axios.put(API(`/ordering/order-items/${id}/status`), { status }); load(); }
    catch (e) { toast.error(e?.response?.data?.error || tr('Save failed.')); }
  };

  // group by order
  const groups = [];
  const byOrder = new Map();
  rows.forEach((r) => {
    if (!byOrder.has(r.order)) { byOrder.set(r.order, []); groups.push(r.order); }
    byOrder.get(r.order).push(r);
  });

  // Embedded στην Κουζίνα (η λίστα Kitchen View είναι από κάτω): όταν μια κράτηση
  // έχει ΜΟΝΟ ένα πιάτο στην ουρά, τα per-item κουμπιά κρύβονται — το Done/Served
  // της γραμμής στη λίστα τα καλύπτει (bulk), ώστε να μην υπάρχουν διπλά χειριστήρια.
  const perReservation = new Map();
  rows.forEach((r) => {
    if (r.reservation) perReservation.set(r.reservation, (perReservation.get(r.reservation) || 0) + 1);
  });
  const buttonsHidden = (it) =>
    embedded && it.reservation && perReservation.get(it.reservation) === 1;

  if (embedded && rows.length === 0) return null;
  const ageColor = (age) => (age >= 15 ? '#dc2626' : age >= 8 ? '#d97706' : '#16a34a');
  return (
    <div className={`st${embedded ? ' is-embedded' : ''}`}>
      <div className="st-head">
        <h2 className="st-title">
          {embedded ? tr('Orders queue') :
            ({ bar: tr('Bar screen'), bbq: tr('BBQ screen'), salads: tr('Salads screen'),
               desserts: tr('Desserts screen') }[station] || tr('Kitchen screen'))}
        </h2>
        <span className="st-badge">{rows.length}</span>
      </div>
      {!embedded && rows.length === 0 && <div className="st-empty">✓ {tr('No pending items.')}</div>}
      <div className="st-grid">
        {groups.map((oid) => {
          const items = byOrder.get(oid);
          const age = ageMin(items[0].sent_at);
          const orderPrefs = parsePrefs(items[0].order_prefs);
          return (
            <div key={oid} className="st-ticket" style={{ '--age': ageColor(age) }}>
              <div className="st-ticket-head">
                <span className="st-table"><small>{tr('Table')}</small>{items[0].tables || '—'}</span>
                <span className="st-age">{age}′</span>
              </div>
              {(orderPrefs.length > 0 || items[0].order_comment) && (
                <div className="st-order-notes">
                  {orderPrefs.length > 0 && (
                    <div className="st-tags">{orderPrefs.map((p) => <span key={p} className="st-tag">⚠ {p}</span>)}</div>
                  )}
                  {items[0].order_comment && <div>🧾 {items[0].order_comment}</div>}
                </div>
              )}
              {items.map((it) => (
                <div key={it.id} className="st-item">
                  <div className="st-item-line">
                    <span className="st-qty">{it.qty}×</span>
                    <span className="st-name">{it.item_name}</span>
                    <span className="st-course">{it.course_name || COURSES[it.course] || ''}</span>
                  </div>
                  {parsePrefs(it.prefs).length > 0 && <div className="st-pref">⚠ {parsePrefs(it.prefs).join(' · ')}</div>}
                  {it.note && <div className="st-note">✎ {it.note}</div>}
                  <div className="st-controls">
                    {!buttonsHidden(it) && (
                      <>
                        {it.status === 'sent' && (
                          <button className="st-btn" onClick={() => setStatus(it.id, 'preparing')}>{tr('preparing')}</button>
                        )}
                        {it.status !== 'ready' && (
                          <button className="st-btn st-btn--ready" onClick={() => setStatus(it.id, 'ready')}>{tr('ready')}</button>
                        )}
                        {it.status === 'ready' && (
                          <button className="st-btn st-btn--served" onClick={() => setStatus(it.id, 'served')}>{tr('served')}</button>
                        )}
                      </>
                    )}
                    <span className={`st-status is-${it.status}`}>
                      {buttonsHidden(it) ? `${tr(it.status)} · ${tr('via the list below')}` : tr(it.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StationScreen;
